import type { HeroTimeEntry, HeroUsageMap } from "@/features/timesheetFlow/timesheetFlowApi";
import {
  heroTimeEntryHasFpMarker,
  isNwPlusFpAuftragsart,
} from "@/features/timesheetFlow/lib/auftragsart";

export interface AggregatedHeroTimeEntry extends HeroTimeEntry {
  _uuid: string;
  row_key: string;
  uuids: string[];
  uuid_durations: Record<string, number>;
  usage_state?: "used" | "remaining";
  usage_split?: boolean;
}

type MutableAggregatedHeroTimeEntry = AggregatedHeroTimeEntry & { _comment_parts?: string[] };

function toLocalIsoDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toLocalIsoWithOffset(date: Date): string {
  const pad = (value: number, length = 2) => String(value).padStart(length, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absOffset / 60));
  const offsetMins = pad(absOffset % 60);
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
}

export function getHeroPartnerName(entry: HeroTimeEntry): string {
  return (
    entry.partner_name ||
    (entry.partner && (entry.partner.full_name || entry.partner.name)) ||
    "Unbekannt"
  );
}

function getPersonId(it: HeroTimeEntry): string {
  return String(
    it.partner_id ||
      (it.partner && it.partner.id) ||
      it.partner_name ||
      (it.partner && (it.partner.full_name || it.partner.name)) ||
      "unknown",
  );
}

function isPauseEntry(entry: HeroTimeEntry): boolean {
  return /pause/i.test(String(entry.category_name || ""));
}

export function filterHeroTimesForTimesheet(items: HeroTimeEntry[], auftragsart: string | null) {
  const list = Array.isArray(items) ? items : [];
  if (!isNwPlusFpAuftragsart(auftragsart)) {
    return { items: list, hiddenFpCount: 0 };
  }
  const filtered = list.filter((it) => !heroTimeEntryHasFpMarker(it));
  return {
    items: filtered,
    hiddenFpCount: Math.max(0, list.length - filtered.length),
  };
}

function getHeroUsageEntry(uuid: string, usage: HeroUsageMap) {
  return usage[uuid] ?? null;
}

function getHeroUsedSecondsForUuid(
  uuid: string | null | undefined,
  usage: HeroUsageMap,
  editableInThisTimesheet: Set<string>,
): number | null {
  if (!uuid) return null;
  if (editableInThisTimesheet.has(uuid)) return null;

  const entry = getHeroUsageEntry(uuid, usage);
  if (!entry) return null;

  const usedSeconds = entry.used_seconds;
  if (usedSeconds == null) return null;

  const numericUsed = Number(usedSeconds);
  return Number.isFinite(numericUsed) ? numericUsed : null;
}

function aggregateHeroTimesByDayPerson(items: HeroTimeEntry[]): AggregatedHeroTimeEntry[] {
  const map = new Map<string, AggregatedHeroTimeEntry>();

  for (const it of items || []) {
    if (isPauseEntry(it)) continue;
    if (!it.start) continue;
    const d = new Date(it.start);
    if (Number.isNaN(d.getTime())) continue;

    const dateKey = toLocalIsoDate(d);
    const personId = getPersonId(it);
    const key = `${personId}__${dateKey}`;

    let agg = map.get(key);
    if (!agg) {
      const created: AggregatedHeroTimeEntry = {
        _uuid: key,
        row_key: key,
        uuids: [],
        uuid_durations: {},
        start: toLocalIsoWithOffset(d),
        duration_in_seconds: 0,
        partner_id: it.partner_id,
        partner_name: it.partner_name,
        partner: it.partner,
        partner_account_type: it.partner_account_type || it.partner?.account_type || undefined,
        category_name: "Arbeitszeit",
        comment: "",
      };
      map.set(key, created);
      agg = created;
    }

    if (!agg.partner_account_type) {
      agg.partner_account_type = it.partner_account_type || it.partner?.account_type || undefined;
    }

    const rawUuid = it.uuid || it.hero_uuid || null;
    if (rawUuid) agg.uuids.push(String(rawUuid));
    if (typeof it.duration_in_seconds === "number") {
      agg.duration_in_seconds = (agg.duration_in_seconds || 0) + it.duration_in_seconds;
      if (rawUuid) {
        agg.uuid_durations[String(rawUuid)] = it.duration_in_seconds;
      }
    }
  }

  return Array.from(map.values());
}

function aggregateHeroTimesByDayPersonDiscrete(items: HeroTimeEntry[]): AggregatedHeroTimeEntry[] {
  const out: AggregatedHeroTimeEntry[] = [];
  let syntheticSeq = 0;

  for (const it of items || []) {
    if (isPauseEntry(it)) continue;
    if (!it.start) continue;
    const d = new Date(it.start);
    if (Number.isNaN(d.getTime())) continue;

    const personId = getPersonId(it);
    const dateKey = toLocalIsoDate(d);
    const uuid = it.uuid || it.hero_uuid || null;
    const key = uuid
      ? String(uuid)
      : `${personId}__${dateKey}__${d.getTime()}__s${syntheticSeq++}`;

    const dur = Number.isFinite(it.duration_in_seconds) ? Number(it.duration_in_seconds) : 0;
    out.push({
      _uuid: key,
      row_key: key,
      uuids: uuid ? [String(uuid)] : [],
      uuid_durations: uuid && dur ? { [String(uuid)]: dur } : {},
      start: toLocalIsoWithOffset(d),
      duration_in_seconds: dur,
      partner_id: it.partner_id,
      partner_name: it.partner_name,
      partner: it.partner,
      partner_account_type: it.partner_account_type || it.partner?.account_type || undefined,
      category_name: it.category_name || "Arbeitszeit",
      comment: it.comment || "",
    });
  }

  return out;
}

function splitHeroTimesByUsage(
  items: HeroTimeEntry[],
  usage: HeroUsageMap,
  editableInThisTimesheet: Set<string>,
): AggregatedHeroTimeEntry[] {
  const map = new Map<string, MutableAggregatedHeroTimeEntry>();

  for (const it of items || []) {
    if (isPauseEntry(it)) continue;
    if (!it.start) continue;
    const d = new Date(it.start);
    if (Number.isNaN(d.getTime())) continue;

    const dateKey = toLocalIsoDate(d);
    const personId = getPersonId(it);
    const baseKey = `${personId}__${dateKey}`;
    const uuid = it.uuid || it.hero_uuid || null;
    const durationSeconds = Number.isFinite(it.duration_in_seconds)
      ? Number(it.duration_in_seconds)
      : null;

    const usedSeconds = getHeroUsedSecondsForUuid(uuid ? String(uuid) : null, usage, editableInThisTimesheet);
    const durationValue = durationSeconds ?? 0;
    const hasDuration = durationSeconds != null && durationSeconds > 0;

    const segments: Array<{ state: "used" | "remaining"; seconds: number | null; isSplit: boolean }> = [];

    if (usedSeconds == null || usedSeconds <= 0) {
      segments.push({ state: "remaining", seconds: durationSeconds, isSplit: false });
    } else if (!hasDuration || usedSeconds >= durationValue) {
      segments.push({
        state: "used",
        seconds: hasDuration ? durationSeconds : usedSeconds,
        isSplit: false,
      });
    } else {
      segments.push({ state: "used", seconds: usedSeconds, isSplit: true });
      segments.push({ state: "remaining", seconds: durationValue - usedSeconds, isSplit: true });
    }

    segments.forEach((segment) => {
      const segmentKey = segment.state === "remaining" ? baseKey : `${baseKey}__used`;
      const rowKey = segment.isSplit
        ? `${baseKey}__${segment.state === "used" ? "used" : "remaining"}`
        : segmentKey;

      let agg = map.get(segmentKey);
      if (!agg) {
        const created: MutableAggregatedHeroTimeEntry = {
          _uuid: segmentKey,
          row_key: rowKey,
          usage_state: segment.state,
          usage_split: false,
          uuids: [],
          uuid_durations: {},
          start: toLocalIsoWithOffset(d),
          duration_in_seconds: 0,
          partner_id: it.partner_id,
          partner_name: it.partner_name,
          partner: it.partner,
          partner_account_type: it.partner_account_type || it.partner?.account_type || undefined,
          category_name: "Arbeitszeit",
          comment: "",
          _comment_parts: [],
        };
        map.set(segmentKey, created);
        agg = created;
      }

      if (!agg.partner_account_type) {
        agg.partner_account_type = it.partner_account_type || it.partner?.account_type || undefined;
      }

      agg.usage_split = agg.usage_split || segment.isSplit;

      if (uuid) {
        agg.uuids.push(String(uuid));
        if (Number.isFinite(segment.seconds)) {
          agg.uuid_durations[String(uuid)] =
            (agg.uuid_durations[String(uuid)] || 0) + (segment.seconds as number);
        }
      }

      if (Number.isFinite(segment.seconds)) {
        agg.duration_in_seconds = (agg.duration_in_seconds || 0) + (segment.seconds as number);
      }

      const startIso = toLocalIsoWithOffset(d);
      if (startIso < (agg.start || "")) {
        agg.start = startIso;
      }

      const comment = String(it.comment || "").trim();
      if (comment && !agg._comment_parts?.includes(comment)) {
        agg._comment_parts = [...(agg._comment_parts || []), comment];
      }
    });
  }

  return Array.from(map.values()).map((agg) => {
    if (agg._comment_parts?.length) {
      agg.comment = agg._comment_parts.join(" · ");
    }
    const { _comment_parts: _, ...rest } = agg;
    return rest;
  });
}

function splitHeroTimesByUsageDiscrete(
  items: HeroTimeEntry[],
  usage: HeroUsageMap,
  editableInThisTimesheet: Set<string>,
): AggregatedHeroTimeEntry[] {
  const results: MutableAggregatedHeroTimeEntry[] = [];

  for (const it of items || []) {
    if (isPauseEntry(it)) continue;
    if (!it.start) continue;
    const d = new Date(it.start);
    if (Number.isNaN(d.getTime())) continue;

    const dateKey = toLocalIsoDate(d);
    const personId = getPersonId(it);
    const baseKey = `${personId}__${dateKey}`;
    const uuid = it.uuid || it.hero_uuid || null;
    const durationSeconds = Number.isFinite(it.duration_in_seconds)
      ? Number(it.duration_in_seconds)
      : null;

    const usedSeconds = getHeroUsedSecondsForUuid(uuid ? String(uuid) : null, usage, editableInThisTimesheet);
    const durationValue = durationSeconds ?? 0;
    const hasDuration = durationSeconds != null && durationSeconds > 0;

    const segments: Array<{ state: "used" | "remaining"; seconds: number | null; isSplit: boolean }> = [];

    if (usedSeconds == null || usedSeconds <= 0) {
      segments.push({ state: "remaining", seconds: durationSeconds, isSplit: false });
    } else if (!hasDuration || usedSeconds >= durationValue) {
      segments.push({
        state: "used",
        seconds: hasDuration ? durationSeconds : usedSeconds,
        isSplit: false,
      });
    } else {
      segments.push({ state: "used", seconds: usedSeconds, isSplit: true });
      segments.push({ state: "remaining", seconds: durationValue - usedSeconds, isSplit: true });
    }

    const startIso = toLocalIsoWithOffset(d);
    const commentTrim = String(it.comment || "").trim();
    const commentParts = commentTrim ? [commentTrim] : [];

    segments.forEach((segment) => {
      const segmentKey = segment.state === "remaining" ? baseKey : `${baseKey}__used`;
      const rowKey = segment.isSplit
        ? `${baseKey}__${segment.state === "used" ? "used" : "remaining"}`
        : segmentKey;

      const rawId = uuid || `${personId}__${dateKey}__${d.getTime()}`;
      const aggUuid = segment.isSplit
        ? `${rawId}__${segment.state === "used" ? "used" : "remaining"}`
        : String(rawId);

      const sec = Number.isFinite(segment.seconds) ? (segment.seconds as number) : 0;
      const uuidList = uuid ? [String(uuid)] : [];
      const uuidDurations: Record<string, number> = {};
      if (uuid && Number.isFinite(sec)) {
        uuidDurations[String(uuid)] = sec;
      }

      results.push({
        _uuid: aggUuid,
        row_key: rowKey,
        usage_state: segment.state,
        usage_split: segment.isSplit,
        uuids: uuidList,
        start: startIso,
        duration_in_seconds: sec,
        uuid_durations: uuidDurations,
        partner_id: it.partner_id,
        partner_name: it.partner_name,
        partner: it.partner,
        partner_account_type: it.partner_account_type || it.partner?.account_type || undefined,
        category_name: it.category_name || "Arbeitszeit",
        comment: commentTrim,
        _comment_parts: [...commentParts],
      });
    });
  }

  return results.map((agg) => {
    if (agg._comment_parts?.length) {
      agg.comment = agg._comment_parts.join(" · ");
    }
    const { _comment_parts: _, ...rest } = agg;
    return rest;
  });
}

export function prepareHeroTimesForSuggestions(
  rawItems: HeroTimeEntry[],
  options: {
    auftragsart: string | null;
    showTimeRange: boolean;
    usage: HeroUsageMap;
    editableInThisTimesheet: Set<string>;
  },
): { items: AggregatedHeroTimeEntry[]; hiddenFpCount: number } {
  const filtered = filterHeroTimesForTimesheet(rawItems, options.auftragsart);
  const mergeHeroDayPerson = !options.showTimeRange;

  if (mergeHeroDayPerson) {
    return {
      items: splitHeroTimesByUsage(filtered.items, options.usage, options.editableInThisTimesheet),
      hiddenFpCount: filtered.hiddenFpCount,
    };
  }

  return {
    items: splitHeroTimesByUsageDiscrete(filtered.items, options.usage, options.editableInThisTimesheet),
    hiddenFpCount: filtered.hiddenFpCount,
  };
}

export function getHeroAggregateUsageStatus(
  agg: AggregatedHeroTimeEntry,
  usage: HeroUsageMap,
  editableInThisTimesheet: Set<string>,
): { isFull: boolean; isPartial: boolean } {
  if (agg.usage_state === "used") {
    return { isFull: true, isPartial: false };
  }
  if (agg.usage_state === "remaining") {
    return { isFull: false, isPartial: false };
  }

  if (!Array.isArray(agg.uuids) || !agg.uuids.length) {
    return { isFull: false, isPartial: false };
  }

  const durationMap = agg.uuid_durations || {};
  const totalSeconds =
    Number.isFinite(agg.duration_in_seconds) && (agg.duration_in_seconds || 0) > 0
      ? (agg.duration_in_seconds as number)
      : Object.values(durationMap).reduce((sum, val) => sum + (Number.isFinite(val) ? val : 0), 0);

  let usedSeconds = 0;
  let hasUsage = false;

  for (const uuid of agg.uuids) {
    if (editableInThisTimesheet.has(uuid)) continue;

    const entry = getHeroUsageEntry(uuid, usage);
    if (!entry) continue;

    const used = entry.used_seconds;
    if (used == null) {
      return { isFull: totalSeconds > 0, isPartial: false };
    }

    if (Number.isFinite(used) && used > 0) {
      hasUsage = true;
      const duration = durationMap[uuid] ?? 0;
      if (duration > 0) {
        usedSeconds += Math.min(used, duration);
      } else {
        usedSeconds += used;
      }
    }
  }

  const cappedUsedSeconds =
    totalSeconds > 0 ? Math.min(usedSeconds, totalSeconds) : usedSeconds;
  const isFull = totalSeconds > 0 && cappedUsedSeconds >= totalSeconds;
  const isPartial = hasUsage && !isFull;

  return { isFull, isPartial };
}

export function getHeroUsedTimesheetInfo(
  agg: AggregatedHeroTimeEntry,
  usage: HeroUsageMap,
  editableInThisTimesheet: Set<string>,
): { display_number?: string | null; timesheet_number?: number | null; used_timesheet_id?: number | null } | null {
  if (!agg?.uuids?.length) return null;

  for (const uuid of agg.uuids) {
    if (editableInThisTimesheet.has(uuid)) continue;
    const entry = getHeroUsageEntry(uuid, usage);
    if (!entry) continue;
    if (entry.used_timesheet_id || entry.timesheet_number || entry.display_number) {
      return entry;
    }
  }

  return null;
}

/** Legacy aggregation without usage split – used to resolve tracking UUIDs from aggregate keys. */
export function aggregateHeroTimesLegacy(
  rawItems: HeroTimeEntry[],
  showTimeRange: boolean,
): AggregatedHeroTimeEntry[] {
  const filtered = filterHeroTimesForTimesheet(rawItems, null);
  return showTimeRange
    ? aggregateHeroTimesByDayPersonDiscrete(filtered.items)
    : aggregateHeroTimesByDayPerson(filtered.items);
}

export function ensureHeroEditableBaseline(
  rows: Array<{ hero_uuid?: string | null; hero_row_key?: string | null; hero_uuids?: string[] }>,
  legacyAggregates: AggregatedHeroTimeEntry[],
  currentEditable: Set<string>,
): Set<string> {
  const baseline = new Set<string>(currentEditable);

  for (const row of rows) {
    if (row.hero_uuids?.length) {
      for (const uuid of row.hero_uuids) {
        if (uuid) baseline.add(uuid);
      }
    }

    const key = row.hero_uuid || row.hero_row_key;
    if (!key || !legacyAggregates.length) continue;

    const agg = legacyAggregates.find((it) => it._uuid === key || it.row_key === key);
    if (!agg?.uuids?.length) continue;
    for (const uuid of agg.uuids) {
      baseline.add(uuid);
    }
  }

  return baseline;
}
